import React, { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Typography, Box, Divider, Button } from '@mui/material';

import { useAppSelector, useAppDispatch, usePrevious } from 'hooks';

import { satellitesActions } from 'state/satellites';
import { schedulesActions } from 'state/schedules';
import { orbitsActions } from 'state/orbits';

import VisualiseActivityController from 'controllers/VisualiseSatActivity';

import SatelliteOverview from 'components/satellites/Overview';
import ScheduleSummary from 'components/schedules/Summary';
import MapView from 'components/map/MapView';
import FilterStatus from 'components/schedules/FilterStatus';

import { SatelliteManager, OrbitManager } from 'models';
import { MapControl } from 'controllers';
import Page from 'components/Page';

const GROUND_STATION_REFRESH_RATE = 1000 * 60 * 5; // seconds

interface PageProps {}

const ScheduleDetailedPage: React.FunctionComponent<PageProps> = () => {
    const dispatch = useAppDispatch();

    const { alertId } = useParams();

    const id = Number(alertId);

    const {
        auth,
        satellites,
        schedules: schedulesObject,
    } = useAppSelector((state) => state);

    const schedules = schedulesObject.list.object;

    const [showGlobe, setShowGlobe] = useState(true);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedSatelliteId, setSelectedSatelliteId] =
        useState<SatelliteManager.Satellite['id']>();

    const prevSelectedSatelliteId = usePrevious(selectedSatelliteId);

    const selectedSatellite = satellites.list.object?.find(
        (a) => a.id === selectedSatelliteId,
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [mapDispatch, setMapDispatch] =
        useState<(control: MapControl) => void>();

    const [visualiser, setVisualiser] = useState<VisualiseActivityController>();

    const [isRefreshing, setisRefreshing] = useState(false);
    //const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    //const [schedules, setSchedules] = useState<ScheduleManager.Schedule[]>();

    const [orbits, setOrbits] = useState<OrbitManager.Orbit[]>();

    const getSchedules = () => {
        dispatch(
            schedulesActions.getScheduleList({
                orbit__satellite: selectedSatelliteId,
                limit: 10,
                after_stop_time: new Date(
                    Date.now() - 1000 * 60 * 5,
                ).toISOString(),
                ordering: 'start_time',
                status: statusFilter !== '' ? statusFilter : undefined,
            }),
        );
    };
    //This ensures that the getSchedules() function is called only once the statusFilter has changed. The statusFilter is updated by the child components onClick(onStatusChange) prop.
    useEffect(() => {
        getSchedules();
    }, [statusFilter]);

    const getAllOrbits = () => {
        if (selectedSatelliteId) {
            dispatch(orbitsActions.getOrbitList()).then((results) => {
                setOrbits(results);
            });
        }
    };

    const selectedOrbit = orbits?.find(
        (o) => o.satellite?.id === selectedSatelliteId,
    );

    const refreshSatellite = () => {
        if (!isRefreshing) {
            dispatch(satellitesActions.getSatelliteList()).then(() => {
                setisRefreshing(false);
                getSchedules();
                getAllOrbits();
            });
            setisRefreshing(true);
        }
    };

    const handleMapRegistration = (
        mapDispatcher: (control: MapControl) => void,
    ) => {
        setMapDispatch(mapDispatcher);

        const v = new VisualiseActivityController(
            selectedSatellite ? [selectedSatellite] : [],
            dispatch,
            0,
        );

        setVisualiser(v);

        mapDispatcher(v);
        console.log('Map Registration');
    };

    useEffect(() => {
        if (
            auth.tokens.status === 'finished' &&
            selectedSatelliteId != null &&
            prevSelectedSatelliteId !== selectedSatelliteId
        ) {
            getSchedules();
            getAllOrbits();
            visualiser?.updateSatellites(
                selectedSatellite ? [selectedSatellite] : [],
                0,
            );
        }
    }, [selectedSatelliteId, auth.tokens.status]);

    useEffect(() => {
        let handler: NodeJS.Timeout | null = null;

        if (
            auth.tokens.status === 'finished' &&
            satellites.list.status === 'idle'
        ) {
            dispatch(satellitesActions.getSatelliteList()).then(() => {
                setisRefreshing(false);
            });
            setisRefreshing(true);

            handler = setInterval(() => {
                if (!isRefreshing) {
                    dispatch(satellitesActions.getSatelliteList()).then(() => {
                        setisRefreshing(false);
                    });
                    setisRefreshing(true);
                }
            }, GROUND_STATION_REFRESH_RATE);
        }

        return () => {
            if (handler) clearInterval(handler);
        };
    }, [auth.tokens.status]);

    let latestTle: string;

    if (selectedOrbit?.tle?.name) {
        latestTle = (
            selectedOrbit?.tle?.name + selectedOrbit?.tle?.elememt_set_number
        ).replaceAll(' ', '');
    } else {
        latestTle = '';
    }

    let tleDetails: [string, Date];

    if (selectedOrbit?.tle?.uploaded) {
        tleDetails = [latestTle, selectedOrbit?.tle?.uploaded];
    } else {
        tleDetails = ['', new Date()];
    }

    return (
        <Page>
            <Typography>Schedule Id: {id}</Typography>
            {selectedSatellite && (
                <>
                    {showGlobe && (
                        <Box sx={{ height: 480, mb: 2 }}>
                            {/* <Button
                                sx={{ position: 'relative', top: 0, left: 0 }}
                                onClick={() => setShowGlobe(false)}
                            >
                                Hide Globe
                            </Button> */}
                            <MapView
                                registerMapControl={handleMapRegistration}
                            />
                        </Box>
                    )}
                    {!showGlobe && (
                        <Box
                            sx={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                            <Button
                                variant="outlined"
                                sx={{ mb: 1 }}
                                onClick={() => setShowGlobe(true)}
                            >
                                Show Globe
                            </Button>
                        </Box>
                    )}
                    {tleDetails && (
                        <SatelliteOverview
                            satellite={selectedSatellite}
                            onRefreshClick={refreshSatellite}
                            isRefreshing={isRefreshing}
                            tleDetails={tleDetails}
                        />
                    )}

                    <Divider sx={{ mt: 1 }} />

                    <Box
                        sx={{
                            my: 1,
                            py: 1,
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Recent and Upcoming Schedules
                        </Typography>
                        <Box
                            sx={{
                                //display: 'flex',
                                //alignItems: 'flex-start',
                                //justifyContent: 'flex-end',
                                //paddingRight: 18,
                                paddingBottom: 1,
                            }}
                        >
                            <FilterStatus
                                statusFilter={statusFilter}
                                onStatusChange={setStatusFilter}
                            />
                        </Box>
                        {schedules && schedules.length > 0 && (
                            <Box
                                sx={{
                                    overflow: 'auto',
                                    maxHeight: 640,
                                    display: 'grid',
                                    gap: 1,
                                    columns: 1,
                                    width: '100%',
                                }}
                            >
                                {schedules.map((s) => (
                                    <ScheduleSummary
                                        key={`${s.id}`}
                                        schedule={s}
                                        context="satellite"
                                    />
                                ))}
                            </Box>
                        )}
                        {schedules && schedules.length === 0 && (
                            <Box>
                                <Typography
                                    variant="h6"
                                    align="center"
                                    sx={{ m: 2 }}
                                >
                                    No Schedule&apos;s assigned to this
                                    satellite.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </>
            )}
            {!selectedSatellite && selectedSatelliteId == null && (
                <Typography variant="h6" align="center" sx={{ m: 2 }}>
                    Select A Satellite
                </Typography>
            )}
            {!selectedSatellite && selectedSatelliteId != null && (
                <Typography variant="h6" align="center" sx={{ m: 2 }}>
                    Cannot find Satellite with Id {selectedSatelliteId}
                </Typography>
            )}
        </Page>
    );
};

ScheduleDetailedPage.displayName = 'ScheduleDetailedPage';
export default ScheduleDetailedPage;
